<template>
    <b-overlay
        class="h-100"
        :show="status === 'loading' && !editMode"
    >
        <div class="card-header align-items-start">
            <div class="card-group-title">
                <h4>
                    {{ $t('org.dashboard.Daily Cost') }}
                </h4>
            </div>
            <div
                id="tour-dashboard-step-3"
                :style="{
                    position: 'absolute',
                    right: 0,
                    width: '30px',
                    height: '30px'
                }"
            />
            <b-card-actions-container
                v-if="(!editMode && data.lineChartData.length && !viewTypeIsMobile) || isTourActive"
                class="card-actions-container"
                :open="isVisible"
                @card:collapse="triggerCollapse"
            />
        </div>
        <CardDatesLegend :data="costOfAfterThisMonth"/>
        <BarChart
            :key="selectedMonth"
            class="dashboard-chart mt-2"
            :data="chartData"
            :options="chartOptions"
            :styles="fetchedChartResponsiveStyle"
        />
        <div
            ref="REF_COLLAPSE"
            :style="collapseResponsiveStyle"
        >
            <CardDatesTable
                v-show="!editMode && isVisible"
                :data="data.dates"
                @modal:showDetail="modalShowDetail"
            />
        </div>
    </b-overlay>
</template>
<script>
import BCardActionsContainer from '@/components/BCardActionsContainer'
import CreateVueChart from '@/components/CreateVueChart.js'
import CardDatesTable from './CardDatesTable'
import useSelectMonth from '@/composables/useSelectMonth'
import useCommonData from '@/composables/useCommonData'
import useDashboard from '@/composables/useDashboard'
import useXAxisData from '@/composables/useXAxisData'
import CardDatesLegend from './CardDatesLegend'
import { ref, getCurrentInstance, computed, watch, nextTick, inject, toRefs } from '@vue/composition-api'
import useDashboardRender from '@/composables/useDashboardRender'
import useTour from '@/composables/useTour'
import formattedDate from '@/utils/formattedDate'
export default {
    props: ['editMode', 'isOpen'],
    components: {
        CardDatesLegend,
        CardDatesTable,
        BCardActionsContainer,
        BarChart: CreateVueChart('Bar')
    },
    setup (props, context) {
        const { isTourActive } = useTour()
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const $themeColors = inject('themeColors')
        const { isOpen } = toRefs(props)
        const isVisible = ref(isOpen.value)
        const { getSummaryDates } = useDashboard()
        const {
            viewType
        } = useDashboardRender()
        const lang = computed(() => vm.$i18n.locale)
        const viewTypeIsMobile = computed(() => viewType.value === 'mobile')
        const { selectedMonth } = useSelectMonth()
        const { __getDollar, __toKFormat } = useCommonData()
        const { createThisMonthFullDate } = useXAxisData()
        const currentParams = computed(() => store.getters['$_dashboard/currentParams'])
        const [status, data] = getSummaryDates(currentParams.value)
        const chartResponsiveStyle = ref({
            position: 'absolute',
            top: '86px',
            bottom: 0,
            left: '0',
            right: '0',
            'margin': '0rem 1.7rem 1.5rem 1.7rem'
        })
        const collapseResponsiveStyle = {
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%'
        }

        const labels = computed(() => createThisMonthFullDate(selectedMonth.value))

        const fullChartData = computed(() => {
            return labels.value.reduce((acc, cur) => {
                acc.line.push(data.value.labels.includes(cur) ? data.value.lineChartData[acc.index] : null)
                acc.bar.push(data.value.labels.includes(cur) ? data.value.barChartData[acc.index] : null)
                if (data.value.labels.includes(cur)) {
                    acc.index++
                }
                return acc
            }, {
                line: [],
                bar: [],
                index: 0
            })
        })

        const chartData = computed(() => ({
            labels: labels.value,
            layout: {
                padding: {
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0
                }
            },
            datasets: [
                {
                    type: 'line',
                    yAxisID: 'y2',
                    data: fullChartData.value.line,
                    fill: false,
                    lineTension: 0.3,
                    borderCapStyle: 'round',
                    borderWidth: 3,
                    borderColor: $themeColors.value.primary,
                    pointBorderColor: $themeColors.value.primary,
                    pointBorderWidth: 1,
                    pointRadius: 3,
                    pointBackgroundColor: '#fff'
                },
                {
                    type: 'bar',
                    yAxisID: 'y1',
                    data: fullChartData.value.bar,
                    barPercentage: 0.25,
                    maxBarThickness: 24,
                    borderRadius: 20,
                    backgroundColor: $themeColors.value.warning
                }
            ]
        }))
        const modalShowDetail = (params) => {
            context.emit('modal:showDetail', params)
        }
        const chartOptions = computed(() => ({
            onClick: (e, chartData) => {
                if (chartData && chartData.length === 2) {
                    const item = data.value.dates.find(item => +item.date.substring(8, 10) === (chartData[0]._index + 1))
                    if (!item) return
                    const date = item.date
                    modalShowDetail({ date })
                }
            },
            onHover: function (e, el) {
                e.target.style.cursor = el.length === 2 ? 'pointer' : 'default'
            },
            cornerRadius: 20,
            tooltips: {
                intersect: false,
                callbacks: {
                    labelColor: (tooltipItem, chart) => {
                        return {
                            backgroundColor: $themeColors.value.warning
                        }
                    },
                    title: (tooltipItem) => {
                        if (!tooltipItem.length) return
                        return selectedMonth.value + '-' + tooltipItem[0].label
                    },
                    label: (tooltipItem) => {
                        if (!tooltipItem) return
                        const item = data.value.dates.find(item => item.date.substring(8, 10) === tooltipItem.label)
                        if (!item) return
                        return ` ${__getDollar(item.cost)}`
                    },
                    afterBody: (tooltipItems) => {
                        if (!tooltipItems.length) return
                        const item = data.value.dates.find(item => item.date.substring(8, 10) === tooltipItems[0].label)
                        if (!item) return
                        const products = item.products
                        if (!products?.length) return
                        const len = products.length > 5 ? 5 : products.length
                        let text = ''
                        for (let i = 0; i < len; i++) {
                            text += i === 0 ? '' : '\n'
                            const item = products[i]
                            text += `${item.productCode}: ${__getDollar(item.cost)}`
                        }
                        return text
                    }
                }
            },
            legend: {
                display: false
            },
            scales: {
                xAxes: [
                    {
                        display: true,
                        gridLines: {
                            color: '#dbdbdb',
                            display: false,
                            drawTicks: false,
                            offsetGridLines: false
                        },
                        ticks: {
                            padding: 14,
                            callback: value => formattedDate(lang.value, value, 'day', {}, selectedMonth.value)
                        }
                    }
                ],
                yAxes: [
                    {
                        id: 'y1',
                        type: 'linear',
                        display: true,
                        gridLines: {
                            display: false
                        },
                        ticks: {
                            callback: label => __toKFormat(label, 2),
                            beginAtZero: true,
                            autoSkip: false,
                            count: 6,
                            maxTicksLimit: 6
                        }
                    },
                    {
                        id: 'y2',
                        type: 'linear',
                        position: 'right',
                        gridLines: {
                            display: false
                        },
                        ticks: {
                            callback: label => __toKFormat(label, 2),
                            count: 6,
                            maxTicksLimit: 6
                        }
                    }
                ]
            },
            plugins: {
                noDataBackground: {
                    display: true,
                    imageSrc: require('@/assets/images/empty.png'),
                    text: vm.$t('org.dashboard.No data')
                },
                datalabels: {
                    display: false
                }
            }
        }))
        watch(currentParams, (value, oldValue) => {
            getSummaryDates(value)
        }, {
            deep: true
        })
        const REF_COLLAPSE = ref(null)
        const triggerCollapse = (status) => {
            context.emit('action:showTable', { component: 'card-dates', status })
        }
        watch(isOpen, (value, oldValue) => {
            isVisible.value = value
            nextTick(() => {
                const tableHeight = value ? vm.$refs['REF_COLLAPSE'].offsetHeight : 0
                chartResponsiveStyle.value.bottom = value ? `${tableHeight}px` : 0
            })
        })
        const fetchedChartResponsiveStyle = computed(() => chartResponsiveStyle.value)
        const costOfAfterThisMonth = computed(() => data.value.costOfAfterThisMonth)
        return {
            isTourActive,
            costOfAfterThisMonth,
            status,
            data,
            chartData,
            chartOptions,
            triggerCollapse,
            isVisible,
            modalShowDetail,
            REF_COLLAPSE,
            collapseResponsiveStyle,
            fetchedChartResponsiveStyle,
            viewTypeIsMobile,
            selectedMonth
        }
    }
}
</script>
